import { Component, ElementRef, inject, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, filter } from 'rxjs';
import { selectUserProfile, selectUserTeams } from '@/store/current-user/current-user.selectors';
import { loadCurrentUserTeams } from '@/store/current-user/teams.actions';
import { RoleType } from '@/core/admin/constants/role.constants';
import { UserTeam } from '@/core/admin/models/team.model';
import { MenuVisibilityContext } from '@/core/layout/menu-visibility';
import { APP_REGISTRY_TOKEN } from '@/core/layout/app-registry';
import { ADMIN_REGISTRY_TOKEN } from '@/core/layout/admin-registry';
import { AppMenuItem, buildMainMenuModel, filterMenuTree } from '@/core/layout/main-menu.model';
import { buildAdminMenuModel } from '@/core/layout/admin-menu.model';

@Component({
  selector: 'app-menu, [app-menu]',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule],
  template: `
    <ul class="layout-menu" #menuContainer>
      <ng-container *ngFor="let item of model; let i = index">
        <!-- Only treat items with children as root headers; routed items without children should be clickable -->
        <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="!!item.items"></li>
        <li *ngIf="item.separator" class="menu-separator"></li>
      </ng-container>
    </ul>
  `,
})
export class AppMenu implements OnInit, OnDestroy {
  el: ElementRef = inject(ElementRef);
  private store = inject(Store);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  @ViewChild('menuContainer') menuContainer!: ElementRef;

  // User permission flags
  isSystemAdmin = false;
  isOrganizationAdmin = false;
  hasWorkspaceEnabledTeam = false;
  currentUser: any = null;

  // Route-based menu visibility
  isAdminRoute = false;

  // Main menu model (shown on non-admin pages): "Apps" (one row per
  // registry app, glyph + name, no sub-items) and "System > Administration".
  // Derived in core/layout/main-menu.model.ts (SCRUM-594).
  private readonly registry = inject(APP_REGISTRY_TOKEN);
  private mainMenuModel: AppMenuItem[] = buildMainMenuModel(this.registry);

  // Admin menu model (shown on admin pages): "Administration" (one row per
  // admin registry group, icon + label, no sub-items), then "Apps" > Back to
  // My Workspace (SCRUM-605). Derived in core/layout/admin-menu.model.ts (SCRUM-622).
  private readonly adminRegistry = inject(ADMIN_REGISTRY_TOKEN);
  private adminMenuModel: AppMenuItem[] = buildAdminMenuModel(this.adminRegistry);

  // Visible menu model (filtered based on permissions)
  model: MenuItem[] = this.mainMenuModel; // Start with main menu, will be filtered on init

  ngOnInit(): void {
    // Check initial route
    this.isAdminRoute = this.router.url.startsWith('/admin');

    // Subscribe to route changes
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.isAdminRoute = event.urlAfterRedirects.startsWith('/admin');
        this.updateMenuVisibility();
      });

    // Subscribe to user profile changes
    this.store
      .select(selectUserProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
        this.updateUserPermissions(user);
        this.updateMenuVisibility();
      });

    // WM menu group is data-gated (needs a WM-enabled team unless admin).
    this.store.dispatch(loadCurrentUserTeams());
    this.store
      .select(selectUserTeams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((teams: UserTeam[]) => {
        this.hasWorkspaceEnabledTeam = (teams ?? []).some(
          (t) => t.workspaceManagementEnabled === true,
        );
        this.updateMenuVisibility();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateUserPermissions(user: any): void {
    if (user && user.roles) {
      this.isSystemAdmin = user.roles.some((role: any) => role.type === RoleType.SYSTEM_ADMIN);
      this.isOrganizationAdmin = user.roles.some((role: any) => role.type === RoleType.ORGANIZATION_ADMIN);

      console.log('Menu permissions updated:', {
        isSystemAdmin: this.isSystemAdmin,
        isOrganizationAdmin: this.isOrganizationAdmin,
        userRoles: user.roles,
      });
    } else {
      this.isSystemAdmin = false;
      this.isOrganizationAdmin = false;
    }
  }

  private updateMenuVisibility(): void {
    // Select menu model based on current route
    const baseModel = this.isAdminRoute ? this.adminMenuModel : this.mainMenuModel;
    // Per-item role / WM-team gating and the tree walk live in the shared
    // helpers; deep clone first so the menu items never share objects with
    // the base models (plain data only: glyph keys and prefix strings survive).
    const ctx: MenuVisibilityContext = {
      roles: this.currentUser?.roles?.map((r: any) => r.type as string) || [],
      hasWmEnabledTeam: this.hasWorkspaceEnabledTeam,
    };
    this.model = filterMenuTree(JSON.parse(JSON.stringify(baseModel)), ctx);
  }
}
